import { BaseEdge, type EdgeProps, type Edge as FlowEdge, getStraightPath, useInternalNode } from "@xyflow/react";
import React from "react";

import { getFloatingEdgeParams, getNodeBorderPoint } from "./floatingEdge.utils";

import styles from "./UnionEdge.module.css";

type UnionEdgeData = { coParentId: string; tooltip?: string };

/**
 * Genealogy-style connector for a child of a couple: a single line that starts at the midpoint of
 * the parents' connector (where the marriage/love badge sits) and runs to the child, instead of two
 * separate parent lines. The source node is one parent; the other parent comes from the edge data.
 */
const UnionEdge = ({ data, id, source, target }: EdgeProps<FlowEdge<UnionEdgeData>>) => {
	const parentNode = useInternalNode(source);
	const childNode = useInternalNode(target);
	const coParentNode = useInternalNode(data?.coParentId ?? "");

	if (!parentNode || !childNode || !coParentNode) {
		return null;
	}

	// Same midpoint the couple's badge is rendered at, so the line visually hangs off the rings.
	const { sx, sy, tx, ty } = getFloatingEdgeParams(parentNode, coParentNode);
	const midX = (sx + tx) / 2;
	const midY = (sy + ty) / 2;

	const childPoint = getNodeBorderPoint(childNode, { x: midX, y: midY });
	const [path] = getStraightPath({ sourceX: midX, sourceY: midY, targetX: childPoint.x, targetY: childPoint.y });

	return (
		<>
			<BaseEdge className={styles.unionLine} id={id} path={path} />
			{data?.tooltip && (
				<path className={styles.hoverPath} d={path}>
					<title>{data.tooltip}</title>
				</path>
			)}
		</>
	);
};

export default UnionEdge;
